import { provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import {
  BrowserModule,
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
  withIncrementalHydration,
} from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { BrowserCookiesModule } from '@ngx-utils/cookies/browser';
import { AuthenticationShellModule } from '@rma/accounts/public/orc-authentication';
import { provideRmaUser } from '@rma/accounts/user';
import { provideRmaFeatureFlagsBrowser } from '@rma/common-ui-ng/feature-flags';
import { provideCustomImageLoader } from '@rma/common-ui-ng/images';
import { provideRmaInstance } from '@rma/common-ui-ng/instance';
import {
  provideRmaCdnUrl,
  provideRmaDashboardUrl,
  provideRmaIdentityUrl,
  provideRmaImagesUrl,
  provideRmaStaticImageUrl,
} from '@rma/common-ui-ng/util';
import { provideRmaTranslation } from '@rma/generic/orc/translation';
import { Environment, IS_PRODUCTION, provideRmaLocale } from '@rma/generic/util/environment';
import { FeatureFlagUserIdentificationService } from '@rma/generic/util/feature-flags';
import { errorDecoratorInterceptor, provideRmaHttpClient } from '@rma/generic/util/http';
import { httpCacheInterceptor } from '@rma/generic/util/http-browser-cache';
import { provideRmaMaterial } from '@rma/generic/util/material';
import { provideMetaInitializer } from '@rma/generic/util/meta-service';
import { provideRmaGoogleTracking } from '@rma/generic/util/tracking/analytics';
import { provideRmaHeap } from '@rma/generic/util/tracking/heap';
import { provideRmaTrackJs } from '@rma/generic/util/tracking/track-js';
import { provideLowerCaseUrlSerializer } from '@rma/site/public/orc-layout';
import { Angulartics2Module } from 'angulartics2';
import { environment as devEnvironment } from '../environment/environment';
import { abortErrorInterceptor } from './interceptors/abort.interceptor';
import { routes } from './routing/app.routes';
import { routerConfig, scrollConfig } from './routing/routing.config';

declare let environment: Environment;

const env = IS_PRODUCTION ? environment : devEnvironment;

export const config: ApplicationConfig = {
  providers: [
    importProvidersFrom(BrowserCookiesModule.forRoot(), BrowserModule),
    provideZoneChangeDetection({ eventCoalescing: true, runCoalescing: true }),
    provideAnimations(),
    importProvidersFrom(Angulartics2Module.forRoot(), AuthenticationShellModule.forRoot()),
    provideLowerCaseUrlSerializer(),
    provideMetaInitializer(),
    // provideRmaAuth(),
    provideRmaGoogleTracking(),
    provideRmaHeap(),
    provideRmaHttpClient(),
    provideRmaFeatureFlagsBrowser(env.launchDarklyClientId, FeatureFlagUserIdentificationService),
    provideRmaLocale(),
    provideRmaMaterial(),
    provideRmaTrackJs(),
    provideRmaTranslation(),
    provideRmaUser(),
    provideRouter(routes, withRouterConfig(routerConfig), withInMemoryScrolling(scrollConfig)),
    provideClientHydration(
      withHttpTransferCacheOptions({ includePostRequests: true, includeRequestsWithAuthHeaders: true }),
      withEventReplay(),
      withIncrementalHydration(),
    ),
    provideCustomImageLoader(),
    provideHttpClient(
      withInterceptors([
        abortErrorInterceptor,
        errorDecoratorInterceptor,
        httpCacheInterceptor({ blacklist: ['/v2/users/me', '/users/me/*'], whitelist: ['/reviews/search/withaggregations'] }),
      ]),
      withFetch(),
      withInterceptorsFromDi(),
    ),
    // setting up urls
    provideRmaInstance(env.tenantCode, env.type),
    provideRmaIdentityUrl(),
    provideRmaDashboardUrl(),
    provideRmaImagesUrl(),
    provideRmaStaticImageUrl(),
    provideRmaCdnUrl(),
  ],
};
